import { FilterIcon } from 'lucide-react';
import { type SnapCategory, SNAP_CATEGORIES } from '@/types/database.types';
import { getCategoryColor } from '@/utils/snap';

interface CategoryFilterProps {
  selectedCategories: SnapCategory[];
  onChange: (categories: SnapCategory[]) => void;
  maxCategories?: number;
}

export default function CategoryFilter({ selectedCategories, onChange, maxCategories = 3 }: CategoryFilterProps) {
  const handleToggleCategory = (category: SnapCategory) => {
    if (selectedCategories.includes(category)) {
      onChange(selectedCategories.filter(c => c !== category));
    } else if (selectedCategories.length < maxCategories) {
      onChange([...selectedCategories, category]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  // Function to get display name for categories
  const getCategoryDisplayName = (category: SnapCategory): string => {
    if (category === 'society_ethics') return 'Society ethics';
    if (category === 'personal_growth') return 'Personal growth';
    return category.replace('_', ' ');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <FilterIcon className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-700">Categories:</span>
        </div>
        {selectedCategories.length > 0 && (
          <button
            onClick={handleClearAll}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {SNAP_CATEGORIES.map((category) => {
          const colors = getCategoryColor(category);
          const isSelected = selectedCategories.includes(category);
          return (
            <button
              key={category}
              onClick={() => handleToggleCategory(category)}
              className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors ${
                isSelected
                  ? `bg-primary-600 text-white`
                  : `${colors.bg} ${colors.text} hover:bg-gray-100`
              } ${selectedCategories.length >= maxCategories && !isSelected ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span className="mr-2">{colors.icon}</span>
              {getCategoryDisplayName(category)}
              {isSelected && (
                <span className="ml-1 text-xs">
                  ✕
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selectedCategories.length > 0 && (
        <p className="mt-2 text-sm text-gray-500">
          Showing SNAPs with {selectedCategories.length === 1 ? 'category' : 'categories'}:{' '}
          {selectedCategories.map((cat, i) => (
            <span key={cat}>
              <span className="font-medium capitalize">{getCategoryDisplayName(cat)}</span>
              {i < selectedCategories.length - 1 && ', '}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}